/**
 * Bulk jobs: a change to every item a search matches runs on the server in the background, in chunks (one history
 * entry still). A list follows the jobs it can start: those already running when it opens (you left and came back:
 * leaving never stops one), and those it starts; asked how far they got every second until they finish, then said
 * and the list reloaded.
 */
import { useEffect, useRef, useState } from "preact/hooks";
import { ApiError, api } from "../api/client.ts";
import type { BulkAction, Job, JobList, SpaceOut } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { formatBytes } from "./format.ts";
import { ignore } from "./ignore.ts";
import { spaceApi } from "./paths.ts";
import { toast } from "./toast.ts";

/** How often a running job is asked how far it got. */
const POLL_MS = 1000;
const NOT_FOUND = 404;

/** What a list of items can do to every item it matches. */
export const ITEM_JOBS: readonly BulkAction[] = [
  "archive",
  "unarchive",
  "entity",
  "update",
  "delete",
  "request",
  "normalize",
];

/** What the trash can do to every item of it a search matches. */
export const TRASH_JOBS: readonly BulkAction[] = ["restore", "purge"];

/** What finishing a job says, by what it did. */
function doneText(j: Job): string {
  const count = j.done;
  switch (j.action) {
    case "archive":
      return t("bulk.archived", { count });
    case "unarchive":
      return t("bulk.unarchived", { count });
    case "delete":
      return t("bulk.deleted", { count });
    case "restore":
      return t("trash.restored", { count });
    case "purge":
    case "cleanup":
      return t("trash.purged", { count, size: formatBytes(j.bytes) });
    default:
      return t(`jobs.done.${j.action}`, { count });
  }
}

/** Says how a finished job went: what it did (or where it stopped), and what it left alone. */
export function sayDone(j: Job): void {
  if (j.error) {
    toast(t("jobs.failed", { done: j.done, total: j.total }), "error");
  } else if (j.done > 0) {
    toast(doneText(j));
  }
  if (j.skipped > 0) {
    toast(t("bulk.skipped", { count: j.skipped }), "error");
  }
  if (j.not_allowed > 0) {
    toast(t("jobs.notAllowed", { count: j.not_allowed }), "error");
  }
}

/** Follows one job started away from a list (a field's Normalize) until it's done, then says how it went. */
export async function followJob(space: SpaceOut, job: Job): Promise<void> {
  let j = job;
  while (j.running) {
    await new Promise((done) => setTimeout(done, POLL_MS));
    j = await api.get<Job>(spaceApi(space, `/jobs/${j.id}`));
  }
  sayDone(j);
}

export interface Jobs {
  /** The job running (the first, if more), to show. */
  running: Job | undefined;
  /** Follows a job just started. */
  follow: (job: Job) => void;
}

/** The jobs doing one of `actions` in `space` (running when it mounts, or started here), followed until done. */
export function useJobs(space: SpaceOut, actions: readonly BulkAction[], onFinish: (job: Job) => void): Jobs {
  const [jobs, setJobs] = useState<readonly Job[]>([]);
  const finished = useRef(onFinish);
  finished.current = onFinish;
  const key = actions.join();
  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` stands for `actions`.
  useEffect(() => {
    const ctl = new AbortController();
    api
      .get<JobList>(spaceApi(space, "/jobs"), { running: true }, ctl.signal)
      .then((r) => setJobs((had) => [...had, ...r.data.filter((j) => actions.includes(j.action))]))
      .catch(ignore);
    return () => ctl.abort();
  }, [space.id, key]);
  useEffect(() => {
    if (jobs.length === 0) {
      return;
    }
    const ctl = new AbortController();
    const timer = setTimeout(async () => {
      const now = await Promise.all(
        jobs.map((j) =>
          api
            .get<Job>(spaceApi(space, `/jobs/${j.id}`), undefined, ctl.signal)
            // Gone (the server restarted): stopped where it was; anything else, asked again next time.
            .catch((e: unknown) =>
              e instanceof ApiError && e.status === NOT_FOUND ? { ...j, running: false, error: e.code } : j,
            ),
        ),
      );
      if (ctl.signal.aborted) {
        return;
      }
      for (const j of now.filter((x) => !x.running)) {
        finished.current(j);
      }
      setJobs(now.filter((x) => x.running));
    }, POLL_MS);
    return () => {
      clearTimeout(timer);
      ctl.abort();
    };
  }, [jobs, space]);
  return {
    running: jobs[0],
    follow: (job) => setJobs((had) => [...had.filter((j) => j.id !== job.id), job]),
  };
}
